import ExpandCollapseButtonGroup from '@components/FilterV3/components/ExpandCollapseButtonGroup/ExpandCollapseButtonGroup';
import ToolbarPopover from '@components/FilterV3/components/ToolbarPopover/ToolbarPopover';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Divider, Stack } from '@mui/material';
import { SlidersHorizontal } from 'lucide-react';
import { MouseEvent } from 'react';
import { useMenu } from '../../../../../../hooks/useMenu';
import LangEnum from '../../../../../../lang/LangEnum';
import { OrderProductsViewTestIds } from '../../../../../../utils/testIds/OrderProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import OrderProductsViewSelectRow from './components/OrderProductsViewSelectRow';
import OrderProductsViewSwitchRow from './components/OrderProductsViewSwitchRow';
import {
  PANEL_ANCHOR_ORIGIN,
  PANEL_TRANSFORM_ORIGIN,
  PANEL_WIDTH,
  panelPaperSx,
} from './orderProductsViewButton.styles';
import { OrderProductsViewConfig } from './orderProductsViewButton.types';

interface OrderProductsViewButtonViewProps extends OrderProductsViewConfig {
  isCollapsed: boolean;
  onOpenPanel: () => void;
}

const OrderProductsViewButtonView = ({
  isCollapsed,
  groupBy,
  subGroupBy,
  sortBy,
  sections,
  orderTotals,
  footer,
  onOpenPanel,
  onDiscardChanges,
}: OrderProductsViewButtonViewProps) => {
  const { translate } = useTranslation();
  const { anchorEl, isOpen: isPanelOpen, open, close } = useMenu();

  const handleOpenPanel = (event: MouseEvent<HTMLElement>) => {
    onOpenPanel();
    open(event);
  };

  const toggleLabel = translate(LangEnum.GLOBAL_VIEW);

  const closePanel = () => {
    close();
    onDiscardChanges();
  };

  const handleApply = () => {
    footer.onApply();
    close();
  };

  return (
    <>
      {isCollapsed ? (
        <IconButton
          icon={SlidersHorizontal}
          color="secondary"
          shape="round"
          isActive={isPanelOpen}
          ariaLabel={toggleLabel}
          ariaExpanded={isPanelOpen}
          testId={OrderProductsViewTestIds.TOGGLE_BUTTON}
          onClick={handleOpenPanel}
        />
      ) : (
        <Button
          variant="contained"
          color="secondary"
          isActive={isPanelOpen}
          testId={OrderProductsViewTestIds.TOGGLE_BUTTON}
          startIcon={<Icon icon={SlidersHorizontal} size="sm" />}
          onClick={handleOpenPanel}
        >
          {toggleLabel}
        </Button>
      )}

      <ToolbarPopover
        anchorEl={anchorEl}
        open={isPanelOpen}
        testId={OrderProductsViewTestIds.PANEL}
        width={PANEL_WIDTH}
        anchorOrigin={PANEL_ANCHOR_ORIGIN}
        transformOrigin={PANEL_TRANSFORM_ORIGIN}
        paperSx={panelPaperSx}
        onClose={closePanel}
      >
        <Stack sx={{ px: 1.5, pt: 1.5, pb: 1, gap: 1 }}>
          <OrderProductsViewSelectRow
            label={LangEnum.ORDER_PRODUCTS_SORT_BY_LABEL}
            testId={OrderProductsViewTestIds.SORT_BY_TRIGGER}
            config={sortBy}
          />
          <OrderProductsViewSelectRow
            label={LangEnum.ORDER_PRODUCTS_GROUPY_BY_LABEL}
            testId={OrderProductsViewTestIds.GROUP_BY_TRIGGER}
            config={groupBy}
          />
          {subGroupBy.isVisible && (
            <OrderProductsViewSelectRow
              label={LangEnum.ORDER_PRODUCTS_SUBGROUP_BY_LABEL}
              testId={OrderProductsViewTestIds.SUBGROUP_BY_TRIGGER}
              config={subGroupBy}
            />
          )}
          {sections.isVisible && (
            <ExpandCollapseButtonGroup
              fullWidth
              collapse={{
                label: LangEnum.ORDER_PRODUCTS_HIDE_ALL_LABEL,
                testId: OrderProductsViewTestIds.HIDE_SECTIONS_BUTTON,
                onClick: sections.onCollapseAll,
              }}
              expand={{
                label: LangEnum.ORDER_PRODUCTS_EXPAND_ALL_LABEL,
                testId: OrderProductsViewTestIds.EXPAND_SECTIONS_BUTTON,
                onClick: sections.onExpandAll,
              }}
            />
          )}
        </Stack>

        {orderTotals.isVisible && (
          <>
            <Divider sx={{ mx: 1.5 }} />
            <Stack sx={{ px: 1.5, py: 1 }}>
              <OrderProductsViewSwitchRow
                label={LangEnum.ORDER_PRODUCTS_HIDE_ORDER_TOTALS_LABEL}
                testId={OrderProductsViewTestIds.HIDE_ORDER_TOTALS_SWITCH}
                config={orderTotals}
              />
            </Stack>
          </>
        )}

        <Divider />

        <Stack direction="row" sx={{ px: 1, py: 1, gap: 0.5, justifyContent: 'flex-end' }}>
          <Button
            variant="text"
            color="secondary"
            size="small"
            disabled={!footer.canRestore}
            testId={OrderProductsViewTestIds.RESTORE_BUTTON}
            onClick={footer.onRestore}
          >
            {translate(LangEnum.GLOBAL_RESTORE)}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!footer.canApply}
            testId={OrderProductsViewTestIds.APPLY_BUTTON}
            onClick={handleApply}
          >
            {translate(LangEnum.GLOBAL_APPLY)}
          </Button>
        </Stack>
      </ToolbarPopover>
    </>
  );
};

export default OrderProductsViewButtonView;
